import { reportNumber } from "@/profitability/report-number";
import type {
  ReportColumn,
  ReportDataset,
  ReportObject,
  ReportOutput,
  ReportRow,
  ReportValue,
} from "@/profitability/types";

export interface RecordObject extends ReportObject {
  dataset: ReportDataset;
  plural: string;
  nameColumn: ReportColumn;
}

/**
 * `name_column` as the API resolves it for a job costing output; the rest
 * covers a ServiceTitan report's, which is served as its script wrote it.
 */
const nameColumnKey = (dataset: ReportDataset) =>
  dataset.name_column ??
  dataset.object?.detail?.title ??
  dataset.key_columns?.[0];

/**
 * The columns a record's id is made of: the API's `record_key_columns`
 * (its key columns, else its name column). The fallbacks cover a
 * ServiceTitan report's output, served as written, by the same rule.
 */
export const recordKeyColumns = (dataset: ReportDataset): string[] => {
  if (dataset.record_key_columns?.length) return dataset.record_key_columns;
  if (dataset.key_columns?.length) return dataset.key_columns;
  const name = nameColumnKey(dataset);
  return name ? [name] : [];
};

export const objectsOf = (output: ReportOutput | undefined): RecordObject[] =>
  (output?.datasets ?? []).flatMap((dataset) => {
    const nameKey = nameColumnKey(dataset);
    const nameColumn = dataset.columns.find((column) => column.key === nameKey);
    if (!dataset.object || !nameColumn) return [];
    return [
      {
        ...dataset.object,
        // The API sends the plural resolved; this is only for an old output.
        plural: dataset.object.plural ?? `${dataset.object.name}s`,
        dataset,
        nameColumn,
      },
    ];
  });

/**
 * A record's id, the way the API names it (`record_id`): a JSON array of its
 * key values as strings, so a value with a "/" in it (`1234/A`) or a
 * composite key stays one id. Opaque: compared whole, never split.
 */
export const recordId = (dataset: ReportDataset, row: ReportRow) =>
  JSON.stringify(
    recordKeyColumns(dataset).map((column) => String(row[column] ?? "")),
  );

/** A rolled-up figure of the row that adds up only what is known (`partial`). */
export const partialFigure = (
  dataset: ReportDataset,
  row: ReportRow,
  key: string,
) =>
  dataset.partial ? dataset.partial[recordId(dataset, row)]?.[key] : undefined;

export const findRecord = (dataset: ReportDataset, id: string) =>
  dataset.rows.find((row) => recordId(dataset, row) === id);

export const recordName = (object: RecordObject, row: ReportRow) =>
  String(row[object.nameColumn.key] ?? "");

/**
 * A record's title where it stands alone: its name, and a number with its
 * object's name before it ("Chen", "Job 48217").
 */
export const recordTitle = (object: RecordObject, row: ReportRow) => {
  const name = recordName(object, row);
  return /^\d/.test(name) ? `${object.name} ${name}` : name;
};

/**
 * Whether a value in a column referencing `target` points at `row`. A
 * reference holds the record's key (its id's one value, or the id whole)
 * or, in a report that links by name, its name.
 */
const pointsAtKey = (target: RecordObject, row: ReportRow, value: string) => {
  const keys = recordKeyColumns(target.dataset);
  return (
    value === recordId(target.dataset, row) ||
    (keys.length === 1 && value === String(row[keys[0]] ?? ""))
  );
};

export const findReferenced = (target: RecordObject, value: ReportValue) => {
  if (value === null || value === "") return undefined;
  const text = String(value);
  const rows = target.dataset.rows;
  return (
    rows.find((row) => pointsAtKey(target, row, text)) ??
    rows.find((row) => recordName(target, row) === text)
  );
};

export const referenceName = (target: RecordObject, value: ReportValue) => {
  const row = findReferenced(target, value);
  return row ? recordName(target, row) : String(value ?? "");
};

/**
 * The record a reference opens: the `target` row it names, else (a record
 * not in this output, still in its history or findings) the id the value
 * would be as the record's one key.
 */
export const referenceRef = (
  target: RecordObject,
  value: ReportValue,
): RecordRef => {
  const row = findReferenced(target, value);
  return {
    dataset: target.dataset.id,
    recordId: row
      ? recordId(target.dataset, row)
      : JSON.stringify([String(value ?? "")]),
  };
};

/** A record named differently from its id still finds its links. */
export const relatedRows = (
  object: RecordObject,
  target: RecordObject,
  row: ReportRow,
) => {
  const columns = object.dataset.columns.filter(
    (column) => column.references === target.dataset.id,
  );
  return object.dataset.rows.filter((item) =>
    columns.some((column) => {
      const value = item[column.key];
      return value !== undefined && findReferenced(target, value) === row;
    }),
  );
};

export const flagColumn = (dataset: ReportDataset) =>
  dataset.columns.find((column) => column.flag);

export const roleColumn = (dataset: ReportDataset, role: string) =>
  dataset.columns.find((column) => column.role === role);

export const rowMargin = (dataset: ReportDataset, row: ReportRow) => {
  const total = roleColumn(dataset, "total");
  const result = roleColumn(dataset, "result");
  // A total or result not in yet is missing, and so is the margin: never a
  // margin worked out from a zero that is not there (FIN-004).
  if (!total || !result) return null;
  const whole = reportNumber(row[total.key]);
  const part = reportNumber(row[result.key]);
  return whole && part !== null ? part / whole : null;
};

export interface RecordRef {
  dataset: string;
  recordId: string;
}

export const sameRecord = (a: RecordRef | null, b: RecordRef | null) =>
  !!a && !!b && a.dataset === b.dataset && a.recordId === b.recordId;

export const recordLabel = (objects: RecordObject[], ref: RecordRef) => {
  const object = objects.find((item) => item.dataset.id === ref.dataset);
  const row = object && findRecord(object.dataset, ref.recordId);
  if (!object) return "Record";
  return row ? `${object.name} ${recordName(object, row)}` : object.name;
};

export const unitOf = (
  object: RecordObject,
  objects: RecordObject[],
  row: ReportRow,
) => {
  const column = object.dataset.columns.find(
    (item) =>
      item.references &&
      objects.find((target) => target.dataset.id === item.references)?.icon ===
        "team",
  );
  const value = column ? row[column.key] : null;
  return value ? String(value) : null;
};

/**
 * The records a row links to, one per column that references a record: its
 * unit, its technician. Each is its own record, even when two share a name.
 */
export const rowLinks = (
  object: RecordObject | undefined,
  objects: RecordObject[],
  row: ReportRow,
) =>
  (object?.dataset.columns ?? []).flatMap((column) => {
    const target = column.references
      ? objects.find((item) => item.dataset.id === column.references)
      : undefined;
    const value = row[column.key];
    return target && value
      ? [{ key: column.key, target, value, name: referenceName(target, value) }]
      : [];
  });
